import type { Dict } from "../core.js";
import type { mailStatus as vi } from "../vi/mailStatus.js";

export const mailStatus: Dict<typeof vi> = {
  title: "Mail readiness",
  subtitle:
    "The hub asks each app's server directly (HMAC-signed, never through the browser): what it sends with, whether the provider still accepts it, whether real mail goes out.",
  loadFailed: "Couldn't load mail status.",
  loading: "Asking the apps…",
  refresh: "Ask again",
  checkedAt: "Asked at {at}",
  summary: "{ready} ready · {degraded} degraded · {down} down · {unknown} not measured · {total} apps",
  "col.app": "App",
  "col.state": "State",
  "col.provider": "Sends with",
  "col.sender": "Sender domain",
  "col.verify": "Provider",
  "col.critical": "Critical mail · 1 h",
  "col.queue": "Queue",
  "col.reasons": "Reasons",
  "state.ready": "Ready",
  "state.degraded": "Degraded",
  "state.down": "Down",
  "outcome.not_configured": "Hub not configured",
  "outcome.not_wired": "Not wired yet",
  "outcome.unauthorized": "Signature rejected",
  "outcome.unreachable": "Unreachable",
  "outcome.invalid_response": "Contract mismatch",
  "hint.not_configured": "The hub has no URL or read secret for this app.",
  "hint.not_wired": "The app has not adopted the mail-status standard yet (404).",
  "hint.unauthorized": "The app rejected the signature — secrets differ, or clocks are more than 5 minutes apart.",
  "hint.unreachable": "Couldn't reach the app (network, 5xx or over 10 seconds).",
  "hint.invalid_response": "The reply doesn't match the contract — the two sides run different kit versions.",
  "reason.STUB_IN_PRODUCTION": "Production runs stub — no mail is sent",
  "reason.FELL_BACK_TO_STUB": "A real provider is declared but config is missing, so it runs stub",
  "reason.STUB_OUTSIDE_PRODUCTION": "Runs stub (outside production)",
  "reason.TRANSPORT_PROBLEM": "Mail config is incomplete",
  "reason.PROVIDER_REJECTED_KEY": "The provider rejected the API key",
  "reason.SENDER_DOMAIN_NOT_VERIFIED": "Sender domain not verified",
  "reason.PROVIDER_UNREACHABLE": "Couldn't reach the provider",
  "reason.CRITICAL_MAIL_FAILING": "{kind} mail gave up within 1 hour",
  "reason.CRITICAL_MAIL_RETRYING": "{kind} mail is waiting to retry",
  "reason.MAIL_FAILING": "{kind} mail failing a lot within 1 hour",
  "reason.QUEUE_STALLED": "Mail queue is stuck",
  "reason.NOT_COUNTED": "The app doesn't count sends",
  declaredDiffers: "declared {declared}",
  "verify.ok": "Key OK · domain verified",
  "verify.key_ok": "Key OK (send-only)",
  "verify.invalid_key": "Key rejected",
  "verify.domain_unverified": "Domain not verified",
  "verify.domain_missing": "Domain not in account",
  "verify.unreachable": "Unreachable",
  "verify.unsupported": "No check yet",
  "verify.skipped": "Not checked",
  criticalCounts: "{sent} sent · {failed} failed",
  criticalNone: "none sent",
  queueEmpty: "empty",
  queueStalled: "stuck {minutes} min",
  notCounted: "not counted",
  noReasons: "—",
  "col.route": "Sending route",
  "source.hub": "From bube-hq · v{version}",
  "source.env": "From the app's env (not migrated)",
  "source.none": "No route",
  "source.unknown": "—",
  chooseRoute: "Choose email set",
  "hubRoute.assigned": "Hub assigned: {partner}",
  "hubRoute.not_applicable": "The hub does not manage this app's mail route",
  "hubRoute.no_binding": "The hub has no email set assigned to the app",
  "hubRoute.catalog_disallowed": "Hub: provider {partner} is no longer offered to the app",
  "hubRoute.grandfathered_only": "Hub: only an old, never-tested assignment ({partner})",
  "hubRoute.set_missing": "Hub: the assigned credential set no longer exists ({partner})",
  "hubRoute.set_incomplete": "Hub: the credential set is missing required fields ({partner})",
  "hubRoute.decrypt_failed": "The hub cannot decrypt the set's keys ({partner})",
  "hubRoute.from_missing": "Hub: the credential set has no sender address ({partner})",
  "test.open": "Send test",
  "test.placeholder": "test recipient address",
  "test.send": "Send",
  "test.cancel": "Cancel",
  "test.sending": "Sending…",
  "test.sent": "Sent via {provider} — check the inbox.",
  "test.not_sent": "The app could not send: {detail}",
  "test.rate_limited": "The app already sent 5 test emails this hour.",
  "test.not_configured": "The hub has no URL or secret for this app.",
  "test.not_wired": "The app does not support « Send test » yet.",
  "test.unauthorized": "The app rejected the signature.",
  "test.unreachable": "Could not reach the app.",
  "test.invalid_response": "The app replied outside the contract.",
  "test.failed": "Test send failed.",
  "test.store_unavailable": "The app could not record the test in its database, so it did NOT send — try again later.",
  "test.code.CHALLENGE_EXPIRED": "the test request expired before reaching the app (clock skew?)",
  "test.code.CHALLENGE_TTL_TOO_LONG": "the test request is valid for more than 10 minutes",
  "test.code.CHALLENGE_WRONG_CONSUMER": "the test request is for another app",
  "test.code.ROUTE_NOT_FOUND": "the app has no such route",
  "test.code.ROUTE_NOT_USABLE": "the requested route is missing settings",
  "test.code.NO_ACTIVE_ROUTE": "the app has no usable mail route (it is running on stub)",
  "test.code.CHALLENGE_CONSUMED": "this test request was already used",
  routeHint: "Each app's sending route = its ENABLED email credential set under Integrations (Resend · Elastic Email · Amazon SES). Changing the set takes effect immediately, no deploy.",
  // PLAT-2026-135 slice I — "Mail of one app" page (`/system/mail/:app`).
  "app.back": "← All apps",
  "app.title": "Mail · {name}",
  "app.subtitle": "Status is asked live when the page opens; counts and mails come from the send log the hub copies from the app (every 5 minutes).",
  "app.loading": "Loading…",
  "app.loadFailed": "Could not read the app's mail.",
  "app.open": "Details",
  "app.cursor": "The hub has copied the send log up to seq {seq}",
  "app.cursorNone": "The hub has not copied any send log from this app yet",
  "app.gap": "The hub MISSED send log seq {from}–{to} (detected {at}) — the app purged its log after 30 days before the hub copied it.",
  "app.logReset": "The app's send log was RESET (new database or restored backup) — the hub re-pulled it from the start at {at} (log epoch {epoch}).",
  "app.logResetFrom":
    "The app's send log was RESET (new database or restored backup) — the hub re-pulled it from the start at {at} (log epoch {epoch}). Mail sent after seq {seq} of the old log that the hub had not copied can no longer be seen.",
  "app.section.route": "Send route",
  "app.section.problems": "What is missing / wrong",
  "app.section.counts": "Mail in the last 24 hours",
  "app.section.mails": "Mail",
  "app.section.lifecycle": "Mail lifecycle",
  "app.section.history": "Readiness history",
  "app.route.source.hub": "From bube-hq",
  "app.route.source.hub_lkg": "From bube-hq — last valid set",
  "app.route.source.legacy": "From the app's old env (transition)",
  "app.route.source.stub": "No route — mail does not go out",
  "app.route.applied": "The app applied route set v{version}",
  "app.route.appliedNone": "The app has not received a route set from the hub",
  "app.route.stale": "The latest hub push is broken — the app runs on the last valid set since {at}",
  "app.route.provider": "Sends via {provider} · domain {domain}",
  "app.route.active": "Active route: {route}",
  "app.route.latch": "Failed over {from} ⇒ {to} at {at} (switch {switchId})",
  "app.route.latchNotDurable": "The switch is held in-process only — the app could not write the latch to its database",
  "app.route.degraded": "The app cannot write its send log since {since} ({reason}) — mail still goes out, hub counts may be short",
  "app.problems.none": "Nothing missing",
  "problem.PROVIDER_NOT_SET": "No provider declared",
  "problem.PROVIDER_UNKNOWN": "Unknown provider",
  "problem.API_KEY_MISSING": "Missing key / login settings",
  "problem.FROM_MISSING": "Missing sender address",
  "problem.FROM_INVALID": "Invalid sender address",
  "problem.ROUTE_CONFIG_INVALID": "Route config malformed — the app cannot read it",
  "app.problems.field": "field {field}",
  "app.problems.route": "route {route}",
  "app.counts.empty": "No business mail in the last 24 hours.",
  "app.col.kind": "Kind",
  "app.col.status": "Outcome",
  "app.col.count": "Mails",
  "app.col.mail": "Mail",
  "app.col.attempts": "Attempts",
  "app.col.provider": "Provider",
  "app.col.error": "Error",
  "app.col.last": "Last",
  "app.filter.failed": "Failed only",
  "app.filter.all": "All mail",
  "app.filter.anyKind": "Any kind",
  "app.filter.code": "Error code",
  "app.range.1": "Last 24 hours",
  "app.range.7": "Last 7 days",
  "app.range.30": "Last 30 days",
  "app.filter.apply": "Filter",
  "app.mails.empty": "No mail matches the filter.",
  "app.lifecycle.hint": "Pick a mail in the table above to see all of its attempts.",
  "app.lifecycle.empty": "The hub has not copied any event of this mail.",
  "app.lifecycle.attempt": "Attempt {n}",
  "app.lifecycle.failover": "failed over from route {from} · {reason}",
  "app.lifecycle.newRequest": "new send intent {id}",
  "app.lifecycle.provider": "Provider side (delivery events)",
  "app.lifecycle.close": "Close",
  "app.history.empty": "No samples in this range.",
  "app.history.summary": "Samples: {ready} ready · {degraded} degraded · {down} down · {other} not measured",
  "app.history.changes": "State changes",
  "status.started": "sending",
  "status.accepted": "accepted",
  "status.rejected": "rejected",
  "status.unknown": "unknown outcome",
  "status.stubbed": "no route",
  "purpose.validation": "check mail",
  "kind.otp": "OTP",
  "kind.verify_email": "email verification",
  "kind.password_reset": "password reset",
  "kind.invite": "invitation",
  "kind.login_notice": "sign-in notice",
  "kind.welcome": "welcome",
  "kind.approval": "approval",
  "kind.support": "support",
  "kind.receipt": "receipt",
  "kind.system": "system",
  "kind.other": "other",
  "kind.notice": "notice",
  "kind.lifecycle": "lifecycle",
};
